import { useCallback, useEffect, useMemo, useState } from 'react';
import { areIntervalsOverlapping, isBefore, isEqual, isWithinInterval, startOfDay } from 'date-fns';
import flatMap from 'lodash/flatMap';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import { useHistory } from 'react-router-dom';
import { FilterType } from '@yme/common/constants';
import { mapTranslateToEn } from '@yme/components/Destinations/ExperienceGrid/helpers';
import { DATE_FORMAT } from '@yme/molecules/date-picker/constants';
import { useTranslation } from '@yme/services/i18n';
import { FilterName } from '../../constants';
import { initialDateRange } from './constants';
import { countAllCheckedFilters, filterAndMapUnits, formatDate, getOptions, mapFilterKeys, parseDate, sortFilterOptionsAlphabetically, updateFilterConfig, } from './helpers';
const useFiltersConfig = (data) => {
    const { t } = useTranslation();
    return useMemo(() => [
        {
            title: t('destinations_experiencesGridFilterCategory_label'),
            type: FilterType.CHECKLIST,
            options: getOptions(data, (item) => isArray(item.typeLabel)
                ? item.typeLabel.map((label) => ({
                    label: t(label.trim()),
                    value: isArray(item.typeLabelEn) ? item.typeLabelEn.join(', ') : item.typeLabelEn,
                }))
                : item.typeLabel.split(',').map((label) => ({
                    label: t(label.trim()),
                    value: isArray(item.typeLabelEn) ? item.typeLabelEn.join(', ') : item.typeLabelEn,
                }))),
            id: FilterName.TYPE_LABEL,
        },
        {
            title: t('destinations_experiencesGridFilterPartner_label'),
            type: FilterType.CHECKLIST,
            options: sortFilterOptionsAlphabetically(getOptions(data, (item) => [
                {
                    label: item?.partnerReference?.partnerName ?? '',
                },
            ])),
            id: FilterName.PARTNER_NAME,
        },
        {
            title: t('destinations_experiencesGridFilterVRental_label'),
            type: FilterType.RADIO,
            options: getOptions(data, (item) => [
                {
                    label: t(item.vehicleRental),
                    value: item.vehicleRentalEn,
                },
            ]),
            id: FilterName.VEHICLE_RENTAL,
        },
        {
            title: t('destinations_experiencesGridFilterCountry_label'),
            type: FilterType.CHECKLIST,
            options: getOptions(data, (item) => item.location.split(',').map((city) => ({
                label: city.trim(),
            }))).sort((a, b) => (a.label > b.label ? 1 : -1)),
            id: FilterName.LOCATION,
        },
        {
            title: t('destinations_experiencesGridFilterProduct_label'),
            type: FilterType.CHECKLIST,
            options: sortFilterOptionsAlphabetically(getOptions(data, (item) => item.units
                ? item.units.map((unit) => ({
                    label: unit,
                }))
                : [])),
            id: FilterName.UNITS,
        },
    ], [data, t]);
};
export const useFilter = (destinations) => {
    const { t } = useTranslation();
    const [config, setConfig] = useState([]);
    const [countTotalAppliedValues, setCountTotalAppliedValues] = useState(0);
    const [filteredData, setFilteredData] = useState([]);
    const [filterSearchParams, setFilterSearchParams] = useState([]);
    const filtersConfig = useFiltersConfig(destinations);
    const [analyticsTranslateMap, setAnalyticsTranslateMap] = useState({});
    const [appliedFilters, setAppliedFilters] = useState({
        filters: {},
        dateRange: initialDateRange,
    });
    const [analyticsFilterData, setAnalyticsFilterData] = useState({
        filters: {},
        dateRange: initialDateRange,
    });
    const history = useHistory();
    useEffect(() => {
        setAnalyticsTranslateMap(mapTranslateToEn(filtersConfig));
    }, [filtersConfig]);
    const updateQueryParams = useCallback((appliedFilters) => {
        const queryParams = new URLSearchParams();
        Object.entries(appliedFilters.filters).forEach(([key, values]) => {
            if (values.length) {
                queryParams.set(key, values.join(','));
            }
        });
        if (appliedFilters.dateRange?.from || appliedFilters.dateRange?.to) {
            const dateRangeParam = `${formatDate(appliedFilters.dateRange?.from)}${appliedFilters.dateRange?.to ? ' to ' + formatDate(appliedFilters.dateRange?.to) : ''}`;
            queryParams.set(FilterName.DATE_RANGES, dateRangeParam);
        }
        history.replace({
            search: `?${queryParams.toString()}`,
        });
    }, [history]);
    useEffect(() => {
        setConfig(filtersConfig);
    }, [filtersConfig]);
    const parseFieldToArray = useCallback((fieldValue) => {
        if (Array.isArray(fieldValue)) {
            return fieldValue;
        }
        return fieldValue?.split(', ').map((item) => t(item));
    }, [t]);
    const onSelectFilter = useCallback((key, options) => {
        const newConfig = config.map((filter) => filter.id === key
            ? {
                ...filter,
                options,
            }
            : filter);
        setConfig(newConfig);
        const allSelectedValues = newConfig.reduce((acc, filter) => {
            acc[filter.id] = filter.options
                .filter((option) => option.checked)
                .map((option) => option.label);
            return acc;
        }, {});
        const allSelectedValuesAnalytics = newConfig.reduce((acc, filter) => {
            acc[filter.id] = filter.options
                .filter((option) => option.checked)
                .map((option) => option.value);
            return acc;
        }, {});
        const newAppliedFilters = {
            ...appliedFilters,
            filters: allSelectedValues,
        };
        setAppliedFilters(newAppliedFilters);
        setAnalyticsFilterData({
            ...appliedFilters,
            filters: allSelectedValuesAnalytics,
        });
        updateQueryParams(newAppliedFilters);
    }, [appliedFilters, config, updateQueryParams]);
    const onSelectDateRange = useCallback((date) => {
        const newAppliedFilters = {
            ...appliedFilters,
            dateRange: date || initialDateRange,
        };
        setAppliedFilters(newAppliedFilters);
        setAnalyticsFilterData({
            ...analyticsFilterData,
            dateRange: date || initialDateRange,
        });
        updateQueryParams(newAppliedFilters);
    }, [analyticsFilterData, appliedFilters, updateQueryParams]);
    const onClearFilters = useCallback(() => {
        const clearedConfig = config.map((filter) => {
            const clearedOptions = filter.options.map((option) => ({
                ...option,
                checked: false,
            }));
            return {
                ...filter,
                options: clearedOptions,
            };
        });
        setConfig(clearedConfig);
        setAppliedFilters({
            filters: {},
            dateRange: initialDateRange,
        });
        setAnalyticsFilterData({
            filters: {},
            dateRange: initialDateRange,
        });
        updateQueryParams({
            filters: {},
            dateRange: initialDateRange,
        });
    }, [config, updateQueryParams]);
    useEffect(() => {
        const searchParams = new URLSearchParams(history.location.search);
        const filterKeys = [...filtersConfig.map((filter) => filter.id), FilterName.DATE_RANGES];
        const { updatedAppliedFilters, analyticsFilters } = Array.from(searchParams.keys()).reduce((result, paramName) => {
            if (!filterKeys.includes(paramName)) {
                return result;
            }
            const value = searchParams.get(paramName);
            setFilterSearchParams((prev) => {
                return Array.from(new Set(prev.concat(paramName)));
            });
            if (value) {
                setFilterSearchParams((prev) => {
                    return Array.from(new Set(prev.concat(paramName)));
                });
                if (paramName === FilterName.DATE_RANGES) {
                    const [from, , to] = value.split(' ');
                    result.updatedAppliedFilters.dateRange = {
                        from: from
                            ? parseDate(from, DATE_FORMAT['dd-MM-yyyy'])
                            : result.updatedAppliedFilters.dateRange.from,
                        to: to
                            ? parseDate(to, DATE_FORMAT['dd-MM-yyyy'])
                            : result.updatedAppliedFilters.dateRange.to,
                    };
                }
                else {
                    result.updatedAppliedFilters.filters[paramName] = value.split(',');
                    if (!isEmpty(analyticsTranslateMap)) {
                        result.analyticsFilters.filters[paramName] = value
                            .split(',')
                            .map((v) => analyticsTranslateMap[v] || v);
                    }
                }
            }
            return result;
        }, {
            updatedAppliedFilters: {
                filters: {},
                dateRange: initialDateRange,
            },
            analyticsFilters: {
                filters: {},
                dateRange: initialDateRange,
            },
        });
        setAppliedFilters(updatedAppliedFilters);
        setAnalyticsFilterData({
            dateRange: updatedAppliedFilters.dateRange,
            filters: analyticsFilters.filters,
        });
        const updatedConfig = updateFilterConfig(filtersConfig, updatedAppliedFilters);
        setConfig(updatedConfig);
    }, [analyticsTranslateMap, filtersConfig, history]);
    useEffect(() => {
        const { filters, dateRange } = appliedFilters;
        const newFilteredData = destinations.filter((item) => Object.keys(filters).every((filterKey) => !filters[filterKey].length ||
            filters[filterKey].some((value) => {
                const field = mapFilterKeys[filterKey]
                    ? mapFilterKeys[filterKey](item)
                    : item[filterKey];
                return parseFieldToArray(field)?.includes(value);
            })) &&
            (!item.expDateRanges ||
                item.expDateRanges.some((range) => {
                    const [start, end] = range.split('-').map((date) => {
                        const dateObject = parseDate(date, DATE_FORMAT['dd.MM.yyyy']);
                        return startOfDay(dateObject);
                    });
                    const isStartDateInvalid = start?.toString() === 'Invalid Date';
                    const isEndDateInvalid = end?.toString() === 'Invalid Date';
                    const isEndDateNotExist = end === undefined;
                    const emptyRange = !dateRange?.from && !dateRange?.to;
                    const endDate = isEndDateNotExist ? start : end;
                    if (isStartDateInvalid || isEndDateInvalid) {
                        if (emptyRange) {
                            return true;
                        }
                        else {
                            return false;
                        }
                    }
                    if (!isBefore(start, endDate) && !isEqual(start, endDate)) {
                        return;
                    }
                    if (!dateRange?.to && dateRange.from) {
                        return isWithinInterval(dateRange.from, {
                            start,
                            end: endDate,
                        });
                    }
                    if (dateRange?.from && dateRange.to) {
                        return areIntervalsOverlapping({
                            start,
                            end: endDate,
                        }, {
                            start: dateRange.from,
                            end: dateRange.to,
                        }, {
                            inclusive: true,
                        });
                    }
                    return true;
                })));
        setFilteredData(newFilteredData);
    }, [appliedFilters, destinations, parseFieldToArray]);
    useEffect(() => {
        if (!filteredData.length) {
            return;
        }
        const units = flatMap(filteredData, FilterName.UNITS);
        const unitFilterItem = filtersConfig.find((item) => item.id === FilterName.UNITS);
        if (!unitFilterItem) {
            return;
        }
        setConfig((prev) => {
            return prev.map((item) => {
                if (item.id === FilterName.UNITS) {
                    return {
                        ...item,
                        options: filterAndMapUnits(units, unitFilterItem, item, appliedFilters.filters[FilterName.UNITS] || []),
                    };
                }
                return item;
            });
        });
    }, [filteredData, filtersConfig, appliedFilters.filters]);
    useEffect(() => {
        const checkedFilters = countAllCheckedFilters(config);
        const isDateRangeApplied = appliedFilters.dateRange?.from || appliedFilters.dateRange?.to;
        const totalAppliedFilters = checkedFilters + (isDateRangeApplied ? 1 : 0);
        setCountTotalAppliedValues(totalAppliedFilters);
    }, [appliedFilters.dateRange?.from, appliedFilters.dateRange?.to, config]);
    return {
        config,
        filteredData,
        onSelectFilter,
        onSelectDateRange,
        onClearFilters,
        dateRange: appliedFilters.dateRange,
        countTotalAppliedValues,
        appliedFilters,
        filterSearchParams,
        analyticsFilterData,
    };
};
